import './PrivateRoute.scss';
import React, { useRef, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import Header from '../header/Header';
import SearchInput from '../searchInput/SearchInput';
import { useMiwiStore, useModuleTypesStore, userStore, searchStore, generalStore } from '../../store';
import Config, { BRAND_LETTER, CLIENT_NAME } from '../../js/config/_global';
import Alert from '../alert/Alert';
import Loader from '../loader/Loader';
import Override from '../override/Override';
import Layout from '../../views/layout/Layout';
import { SEARCH_RESOURCES, COOKIE_KEYS } from '../../js/_constants';
import { refreshAuth } from '../../js/api';
import Helper, { toCapitalizedString } from '../../js/helpers';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import { AvpContextClaims, Claims } from '@org-avp/context-claims';

export const PrivateRoute = ({
    component,
}: {
    component: React.ComponentType<any>;
}) => {
    const location = useLocation();
    const { t } = useTranslation();
    const { overrideMedia, overrideDealerId, fetchMiwiDevices, fetchNaapDevices } = useMiwiStore();
    const { getEditorialModuleTypes } = useModuleTypesStore();
    const { getUser, details } = userStore();
    const { setSearchResource } = searchStore();
    const { hideAlert, setAlert, initApp, setAuthenticated, authenticated, loading, isGlobalLoading} = generalStore();

    const performLoggedUserRequest = async () => {
        if (!details.sub) {
            await getUser();
        }
        await getEditorialModuleTypes();
        await initApp();
    };

    useEffect(() => {
        let resource;
        switch (location.pathname) {
            case '/dealerships':
                resource = SEARCH_RESOURCES.DEALERSHIP;
                break;
            case '/devices':
                resource = SEARCH_RESOURCES.DEVICE;
                break;
            case '/playlists':
                resource = SEARCH_RESOURCES.PLAYLIST;
                break;
            default:
                resource = SEARCH_RESOURCES.MEDIA;
                break;
        }
        setSearchResource(resource);
    }, [location.pathname]);

    const prevAuthenticated = useRef(false);

    useEffect(() => {
        if (!prevAuthenticated.current && authenticated) {
            performLoggedUserRequest();
            prevAuthenticated.current = authenticated;
        } else {
            localStorage.setItem('redirectUrl', location.pathname + location.search);
            refreshAuth().then(() => setAuthenticated(true));
        }
    }, [authenticated]);

    useEffect(() => {
        if(!isGlobalLoading) {
            setTimeout(() => {
                const idToken = Helper.getCookie(COOKIE_KEYS.ID_TOKEN) as string;
                if (idToken) {
                    hideAlert();
                    const decodedIdToken = jwtDecode(idToken) as Claims;
                    const userBrands = AvpContextClaims.getBrands(decodedIdToken);
                    if (!userBrands.includes(BRAND_LETTER)) {
                        setAlert({
                            message: `${t('brand_alert_1')} ${toCapitalizedString(CLIENT_NAME)} ${t('brand_alert_2')} ${toCapitalizedString(CLIENT_NAME)} ${t('brand_alert_3')}`,
                            type: 'warning',
                            display: true,
                            isAutoDisappearing: false,
                        });
                    }
                }
            }, 2000);
        }
    }, [isGlobalLoading]);

    const [isOverrideLoginOpen, setIsOverrideLoginOpen] = useState(false);
    const overrideToggle = () => {
        setIsOverrideLoginOpen(!isOverrideLoginOpen);
        !isOverrideLoginOpen && overrideDealerId && fetchMiwiDevices();
        //!isOverrideLoginOpen && overrideDealerId && fetchNaapDevices();
    };

    const renderInnerComponent = () =>
        component
            ? React.createElement(component)
            : null;

    const renderMiwiInterface = () => <Override.MiwiInterface onClose={overrideToggle} />;
    //const renderOverrideInterface = () => <Override.OverrideInterface onClose={overrideToggle} />;

    if (isGlobalLoading) {
        return <Loader />;
    }

    return (
        <div className="private-route">
            <Header />
            <SearchInput />
            <Layout overrideMedia={overrideMedia}>
                {renderInnerComponent()}
                {Config.isOverrideEnabled && (
                    <>
                        <Override.Panel />
                        <Override.OpenButton onMouseUp={overrideToggle} />
                    </>
                )}
            </Layout>
            {Config.isOverrideEnabled && (
                <>
                    <Override.Modal
                        show={isOverrideLoginOpen}
                        onClose={overrideToggle}
                    >
                        {renderMiwiInterface()}
                        {/* {renderOverrideInterface()} */}
                    </Override.Modal>
                </>
            )}
            <Alert />
        </div>
    );
};

export default PrivateRoute;
